import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, ShareNetwork, Copy, Check,
  FacebookLogo, LinkedinLogo, 
  InstagramLogo, TiktokLogo
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const ShareModal = ({ 
  isOpen, 
  onClose, 
  title = "Share this content",
  shareUrl,
  shareText,
  shareType = "post" // "post", "devotional", "course", etc.
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const shareToFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const shareToLinkedIn = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const shareToInstagram = () => {
    // Instagram doesn't have a direct web share API
    // Copy link and show instructions
    handleCopyLink();
    toast.info('Link copied! Open Instagram and paste in your story or post.');
  };

  const shareToTikTok = () => {
    // TikTok doesn't have a direct web share API
    // Copy link and show instructions
    handleCopyLink();
    toast.info('Link copied! Open TikTok and paste in your video description.');
  };

  const socialPlatforms = [
    {
      name: 'Facebook',
      icon: FacebookLogo,
      color: '#1877F2',
      bgColor: 'rgba(24, 119, 242, 0.1)',
      action: shareToFacebook,
      description: 'Share to your timeline'
    },
    {
      name: 'LinkedIn',
      icon: LinkedinLogo,
      color: '#0A66C2',
      bgColor: 'rgba(10, 102, 194, 0.1)',
      action: shareToLinkedIn,
      description: 'Share with your network'
    },
    {
      name: 'Instagram',
      icon: InstagramLogo,
      color: '#E4405F',
      bgColor: 'rgba(228, 64, 95, 0.1)',
      action: shareToInstagram,
      description: 'Copy link for Stories'
    },
    {
      name: 'TikTok',
      icon: TiktokLogo,
      color: '#000000',
      bgColor: 'rgba(255, 255, 255, 0.1)',
      action: shareToTikTok,
      description: 'Copy link for video'
    }
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)' }}
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl overflow-hidden"
            style={{ backgroundColor: '#111', border: '1px solid #222' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: '#222' }}>
              <div className="flex items-center gap-3">
                <ShareNetwork size={24} style={{ color: '#C4A661' }} />
                <h2 className="text-lg font-bold" style={{ color: '#fff' }}>{title}</h2>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              >
                <X size={20} style={{ color: '#888' }} />
              </button>
            </div>

            {/* Share Text Preview */}
            {shareText && (
              <div className="px-5 py-4 border-b" style={{ borderColor: '#222' }}>
                <p className="text-sm line-clamp-2" style={{ color: '#aaa' }}>
                  "{shareText}"
                </p>
              </div>
            )}

            {/* Social Platforms */}
            <div className="p-5">
              <p className="text-xs uppercase tracking-wider mb-4" style={{ color: '#666' }}>
                Share to Social Media
              </p>
              <div className="grid grid-cols-2 gap-3">
                {socialPlatforms.map((platform) => (
                  <button
                    key={platform.name}
                    onClick={platform.action}
                    className="flex items-center gap-3 p-4 rounded-xl transition-all hover:scale-105"
                    style={{ backgroundColor: platform.bgColor, border: `1px solid ${platform.color}30` }}
                  >
                    <platform.icon size={28} weight="fill" style={{ color: platform.color }} />
                    <div className="text-left">
                      <p className="font-semibold text-sm" style={{ color: '#fff' }}>{platform.name}</p>
                      <p className="text-xs" style={{ color: '#888' }}>{platform.description}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Copy Link Section */}
            <div className="px-5 pb-5">
              <p className="text-xs uppercase tracking-wider mb-3" style={{ color: '#666' }}>
                Or Copy Link
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={shareUrl}
                  readOnly
                  className="flex-1 px-4 py-3 rounded-lg text-sm truncate"
                  style={{ 
                    backgroundColor: '#0a0a0a', 
                    border: '1px solid #333',
                    color: '#888'
                  }}
                />
                <button
                  onClick={handleCopyLink}
                  className="px-4 py-3 rounded-lg font-semibold transition-all flex items-center gap-2"
                  style={{ 
                    backgroundColor: copied ? '#10B981' : '#C4A661',
                    color: copied ? '#fff' : '#000'
                  }}
                >
                  {copied ? (
                    <>
                      <Check size={18} weight="bold" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy size={18} />
                      Copy
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* POWR Branding */}
            <div className="px-5 py-4 text-center" style={{ backgroundColor: '#0a0a0a' }}>
              <p className="text-xs" style={{ color: '#444' }}>
                Shared from <span style={{ color: '#C4A661' }}>POWR</span> — Brotherhood. Faith. Wealth.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ShareModal;
